/**
 * Nested unit → element → criteria tree with checkboxes for Cycle Builder.
 * `tiles` is a compact card grid; `list` is the denser saved-template tree.
 */
import React, { useEffect, useRef } from "react";
import { translateUi } from "../i18n/index";
import { ChevronDown, ChevronRight, Plus } from "../icons";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { cb, cbCn } from "./cycleBuilderTheme";
import type { WizardCriteriaItem } from "./CycleWizardStore";
import {
  MIME_LIBRARY,
  collectVisiblePayloadsForElement,
  collectVisiblePayloadsForUnit,
  countVisibleTemplateLinesInElement,
  displayUnitNo,
  isTemplateLineClaimedInCycle,
  makeTemplateLineKey,
  stripReferenceForDisplay,
  type LibraryDragPayload,
  type LibraryPayload,
  type LibraryUnit,
} from "./cycleLibraryUi";

function cn(...inputs: Parameters<typeof clsx>) {
  return twMerge(clsx(inputs));
}

function TriCheckbox({
  checked,
  indeterminate,
  disabled,
  ariaLabel,
  onChange,
}: {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  ariaLabel: string;
  onChange: (next: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate && !checked;
  }, [checked, indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      onClick={(e) => e.stopPropagation()}
      aria-label={ariaLabel}
      className="h-4 w-4 shrink-0 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
    />
  );
}

function tileUnitParts(unit: LibraryUnit, unitTitle?: (unit: LibraryUnit) => string): { num: string; name: string } {
  const title = unitTitle ? unitTitle(unit) : unit.title;
  const m = title.match(/^Unit\s+(\d+)\s*[–-]\s*(.+)$/i);
  if (m) return { num: m[1], name: m[2] };
  return { num: displayUnitNo(unit.id), name: title };
}

function elementShortName(el: { id: string; title: string }): string {
  const rest = el.title.replace(/^element\s*[\d.]+\s*/i, "").trim();
  return rest || el.title;
}

type TreeHandlers = {
  cycleId: string;
  items: WizardCriteriaItem[];
  keyCounts: Map<string, number>;
  selectedLibrary: Record<string, LibraryPayload>;
  pickable: boolean;
  isMobile?: boolean;
  tapToAdd?: boolean;
  isDarkMode: boolean;
  demoLabel: string;
  explainLabel: string;
  expandedElements: Record<string, boolean>;
  onToggleElement: (key: string) => void;
  onToggleLine: (payload: LibraryPayload) => void;
  onSetPayloadsSelected: (payloads: LibraryPayload[], selected: boolean) => void;
  onAddWhole: (payloads: LibraryPayload[]) => void;
  onAddLine: (payload: LibraryPayload, type: "demonstrate" | "explain") => void;
  onLibraryDragStart: (count: number) => void;
  onLibraryDragEnd: () => void;
  getDragLibraryItems: (payload: LibraryPayload) => LibraryPayload[];
};

function CriteriaLines({
  cycleId,
  unit,
  el,
  elKey,
  tree,
}: {
  cycleId: string;
  unit: LibraryUnit;
  el: LibraryUnit["elements"][number];
  elKey: string;
  tree: TreeHandlers;
}) {
  const {
    items,
    keyCounts,
    selectedLibrary,
    pickable,
    isMobile,
    tapToAdd,
    isDarkMode,
    demoLabel,
    explainLabel,
    onToggleLine,
    onAddLine,
    onLibraryDragStart,
    onLibraryDragEnd,
    getDragLibraryItems,
  } = tree;
  const showLineAdd = Boolean(isMobile || tapToAdd);

  const row = (criteria: string, kind: "demonstrate" | "explain", lineIdx: number) => {
    const libraryKey = makeTemplateLineKey(cycleId, unit.id, el.id, kind, lineIdx);
    if (isTemplateLineClaimedInCycle(libraryKey, unit.id, el.id, kind, criteria, items, keyCounts)) {
      return null;
    }
    const display = stripReferenceForDisplay(criteria);
    const payload: LibraryPayload = {
      unit_no: unit.id,
      element_no: el.id,
      element: el.title,
      criteria,
      libraryKey,
    };
    const isSelected = !!libraryKey && !!selectedLibrary[libraryKey];
    const lineNo = lineIdx + 1;
    const textShown = !pickable || display.length <= 140 ? display : `${display.slice(0, 140)}…`;
    return (
      <div
        key={`${elKey}-${kind}-${lineIdx}`}
        className={cn(
          "flex flex-wrap items-start gap-2 rounded-lg px-2 py-1.5",
          isSelected
            ? isDarkMode
              ? "bg-indigo-950/50"
              : "bg-indigo-50"
            : isDarkMode
              ? "hover:bg-slate-800/80"
              : "hover:bg-slate-50"
        )}
      >
        {pickable && (
          <TriCheckbox
            checked={isSelected}
            ariaLabel={translateUi("Select template line {{n}}", { n: lineNo })}
            onChange={() => onToggleLine(payload)}
          />
        )}
        <span
          className={cbCn(
            "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-[10px] font-semibold tabular-nums",
            kind === "demonstrate"
              ? isDarkMode
                ? "bg-emerald-950/70 text-emerald-200"
                : "bg-emerald-100 text-emerald-800"
              : isDarkMode
                ? "bg-amber-950/70 text-amber-200"
                : "bg-amber-100 text-amber-800"
          )}
        >
          {lineNo}
        </span>
        <div
          draggable={pickable && !showLineAdd}
          onDragStart={(e) => {
            if (!pickable) return;
            const draggedItems = getDragLibraryItems(payload);
            onLibraryDragStart(draggedItems.length);
            e.dataTransfer.setData(MIME_LIBRARY, JSON.stringify({ items: draggedItems } satisfies LibraryDragPayload));
            e.dataTransfer.effectAllowed = "copy";
          }}
          onDragEnd={() => onLibraryDragEnd()}
          className={cn(
            "min-w-0 flex-1 text-sm leading-snug",
            isDarkMode ? "text-slate-300" : "text-slate-700",
            pickable && !showLineAdd && "cursor-grab active:cursor-grabbing"
          )}
        >
          {textShown}
        </div>
        {showLineAdd && pickable && (
          <div className="flex w-full gap-2">
            <button type="button" className={cbCn("flex-1", cb.accentBtn(isDarkMode, "emerald"), "py-1.5")} onClick={() => onAddLine(payload, "demonstrate")}>
              → {demoLabel}
            </button>
            <button type="button" className={cbCn("flex-1", cb.accentBtn(isDarkMode, "amber"), "py-1.5")} onClick={() => onAddLine(payload, "explain")}>
              → {explainLabel}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-1 px-2 pb-3 pt-1">
      {el.demonstrate.length > 0 && (
        <p className={cbCn("px-2 pt-1 text-[10px] font-semibold uppercase tracking-wider", isDarkMode ? "text-emerald-400/80" : "text-emerald-700/80")}>
          {translateUi("Demonstrate")}
        </p>
      )}
      {el.demonstrate.map((d, di) => row(d, "demonstrate", di))}
      {el.explain.length > 0 && (
        <p className={cbCn("px-2 pt-2 text-[10px] font-semibold uppercase tracking-wider", isDarkMode ? "text-amber-400/80" : "text-amber-700/80")}>
          {translateUi("Explain")}
        </p>
      )}
      {el.explain.map((ex, ei) => row(ex, "explain", ei))}
    </div>
  );
}

function ElementBlock({
  cycleId,
  unit,
  unitKey,
  compact,
  tree,
}: {
  cycleId: string;
  unit: LibraryUnit;
  unitKey: string;
  compact?: boolean;
  tree: TreeHandlers;
}) {
  const { items, keyCounts, selectedLibrary, pickable, isMobile, isDarkMode, expandedElements, onToggleElement, onSetPayloadsSelected, onAddWhole } =
    tree;

  return (
    <>
      {unit.elements.map((el) => {
        const elKey = `${unitKey}-${el.id}`;
        const isElExpanded = expandedElements[elKey] ?? false;
        const elementVisiblePayloads = collectVisiblePayloadsForElement(cycleId, unit.id, el, items, keyCounts);
        const elementVisibleKeys = elementVisiblePayloads.map((p) => p.libraryKey).filter(Boolean) as string[];
        const selectedInElement = elementVisibleKeys.filter((k) => !!selectedLibrary[k]).length;
        const hasElementVisible = elementVisibleKeys.length > 0;
        const allElementSelected = hasElementVisible && selectedInElement === elementVisibleKeys.length;
        const nc = countVisibleTemplateLinesInElement(cycleId, unit.id, el, items, keyCounts);
        const name = elementShortName(el);

        return (
          <div key={elKey} className={cbCn(compact ? "rounded-xl" : "border-t", compact ? (isDarkMode ? "bg-slate-900/50" : "bg-slate-50/80") : isDarkMode ? "border-slate-700" : "border-slate-100")}>
            <div className={`flex w-full items-center gap-2 text-sm ${isMobile ? "px-3 py-2.5" : compact ? "px-3 py-2" : "px-6 py-2"}`}>
              {pickable && (
                <TriCheckbox
                  checked={allElementSelected}
                  indeterminate={selectedInElement > 0 && !allElementSelected}
                  disabled={!hasElementVisible}
                  ariaLabel={translateUi("Select all criteria in {{title}}", { title: el.title })}
                  onChange={(next) => onSetPayloadsSelected(elementVisiblePayloads, next)}
                />
              )}
              <button
                type="button"
                onClick={() => onToggleElement(elKey)}
                className={cbCn(`flex min-w-0 flex-1 items-center gap-2 text-left ${isMobile ? "min-h-[44px]" : ""}`, isDarkMode ? "hover:bg-slate-800/80" : "hover:bg-white/70")}
              >
                {isElExpanded ? (
                  <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
                )}
                <span
                  className={cbCn(
                    "shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-semibold tabular-nums",
                    isDarkMode ? "bg-slate-700 text-slate-200" : "bg-white text-slate-600 shadow-sm"
                  )}
                >
                  {el.id}
                </span>
                <span className={cbCn("min-w-0 truncate", isDarkMode ? "text-slate-300" : "text-slate-700")}>{name}</span>
                <span className="ml-auto shrink-0 text-xs text-slate-400">{nc}</span>
              </button>
              {pickable && (
                <button
                  type="button"
                  disabled={elementVisiblePayloads.length === 0}
                  onClick={() => onAddWhole(elementVisiblePayloads)}
                  className={cbCn(
                    "shrink-0 rounded-lg px-2 py-1 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50",
                    isDarkMode ? "text-indigo-300 hover:bg-indigo-950/60" : "text-indigo-700 hover:bg-indigo-50"
                  )}
                  title={translateUi("Add entire element (Demonstrate/Explain auto-mapped)")}
                >
                  {translateUi("Add")}
                </button>
              )}
            </div>
            {isElExpanded && <CriteriaLines cycleId={cycleId} unit={unit} el={el} elKey={elKey} tree={tree} />}
          </div>
        );
      })}
    </>
  );
}

export function LibraryCriteriaTree({
  cycleId,
  units,
  items,
  keyCounts,
  selectedLibrary,
  pickable = true,
  defaultExpandUnits = false,
  variant = "list",
  unitTitle,
  isMobile,
  compactTiles,
  horizontalTiles,
  tapToAdd,
  isDarkMode = false,
  demoLabel,
  explainLabel,
  expandedUnits,
  expandedElements,
  onToggleUnit,
  onToggleElement,
  onToggleLine,
  onSetPayloadsSelected,
  onClearSelected,
  onAddWhole,
  onAddSelectedTo,
  onAddLine,
  onLibraryDragStart,
  onLibraryDragEnd,
  getDragLibraryItems,
}: {
  cycleId: string;
  units: LibraryUnit[];
  items: WizardCriteriaItem[];
  keyCounts: Map<string, number>;
  selectedLibrary: Record<string, LibraryPayload>;
  pickable?: boolean;
  defaultExpandUnits?: boolean;
  variant?: "list" | "tiles";
  unitTitle?: (unit: LibraryUnit) => string;
  isMobile?: boolean;
  /** Single-column unit tiles (e.g. side panel). */
  compactTiles?: boolean;
  /** Full-width strip of unit tiles (e.g. collapsible library pane). */
  horizontalTiles?: boolean;
  /** Show per-line Add to Demonstrate/Explain instead of drag. */
  tapToAdd?: boolean;
  isDarkMode?: boolean;
  demoLabel: string;
  explainLabel: string;
  expandedUnits: Record<string, boolean>;
  expandedElements: Record<string, boolean>;
  onToggleUnit: (key: string) => void;
  onToggleElement: (key: string) => void;
  onToggleLine: (payload: LibraryPayload) => void;
  onSetPayloadsSelected: (payloads: LibraryPayload[], selected: boolean) => void;
  onClearSelected: (keys: string[]) => void;
  onAddWhole: (payloads: LibraryPayload[]) => void;
  onAddSelectedTo: (unitPayloads: LibraryPayload[], type: "demonstrate" | "explain") => void;
  onAddLine: (payload: LibraryPayload, type: "demonstrate" | "explain") => void;
  onLibraryDragStart: (count: number) => void;
  onLibraryDragEnd: () => void;
  getDragLibraryItems: (payload: LibraryPayload) => LibraryPayload[];
}) {
  const tree: TreeHandlers = {
    cycleId,
    items,
    keyCounts,
    selectedLibrary,
    pickable,
    isMobile,
    tapToAdd,
    isDarkMode,
    demoLabel,
    explainLabel,
    expandedElements,
    onToggleElement,
    onToggleLine,
    onSetPayloadsSelected,
    onAddWhole,
    onAddLine,
    onLibraryDragStart,
    onLibraryDragEnd,
    getDragLibraryItems,
  };

  const oneColTiles = Boolean(isMobile || (compactTiles && !horizontalTiles));
  const stripTiles = Boolean(horizontalTiles) && !isMobile;

  if (variant === "tiles") {
    return (
      <div
        className={cbCn(
          "grid gap-3",
          isMobile ? "grid-cols-1" : stripTiles ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5" : oneColTiles ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"
        )}
      >
        {units.map((unit) => {
          const unitKey = `${cycleId}-${unit.id}`;
          const isUnitExpanded = expandedUnits[unitKey] ?? defaultExpandUnits;
          const unitVisiblePayloads = collectVisiblePayloadsForUnit(cycleId, unit, items, keyCounts);
          const unitVisibleKeys = unitVisiblePayloads.map((p) => p.libraryKey).filter(Boolean) as string[];
          const selectedInUnit = unitVisibleKeys.filter((k) => !!selectedLibrary[k]).length;
          const hasUnitVisible = unitVisibleKeys.length > 0;
          const allUnitSelected = hasUnitVisible && selectedInUnit === unitVisibleKeys.length;
          const n = unit.elements.reduce(
            (a, el) => a + countVisibleTemplateLinesInElement(cycleId, unit.id, el, items, keyCounts),
            0
          );
          const { num, name } = tileUnitParts(unit, unitTitle);
          const added = n === 0;

          return (
            <div
              key={unitKey}
              className={cbCn(
                "flex flex-col overflow-hidden rounded-2xl border shadow-sm transition-all duration-200",
                isUnitExpanded && (stripTiles ? "col-span-full" : !oneColTiles && "sm:col-span-2"),
                allUnitSelected
                  ? isDarkMode
                    ? "border-indigo-400 bg-indigo-950/40"
                    : "border-indigo-400 bg-indigo-50/80"
                  : selectedInUnit > 0
                    ? isDarkMode
                      ? "border-indigo-500/50 bg-slate-800"
                      : "border-indigo-200 bg-white"
                    : isDarkMode
                      ? "border-slate-700 bg-slate-800/80 hover:border-slate-600"
                      : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-md",
                added && "opacity-60"
              )}
            >
              <div className="flex items-start gap-3 p-3 sm:p-3.5">
                {pickable && (
                  <div className="pt-1">
                    <TriCheckbox
                      checked={allUnitSelected}
                      indeterminate={selectedInUnit > 0 && !allUnitSelected}
                      disabled={!hasUnitVisible}
                      ariaLabel={translateUi("Select all criteria in {{title}}", { title: name })}
                      onChange={(next) => onSetPayloadsSelected(unitVisiblePayloads, next)}
                    />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => onToggleUnit(unitKey)}
                  className={cbCn("flex min-w-0 flex-1 items-start gap-3 text-left", isMobile && "min-h-[44px]")}
                  aria-expanded={isUnitExpanded}
                >
                  <span
                    className={cbCn(
                      "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-sm font-bold tabular-nums",
                      isUnitExpanded || selectedInUnit > 0
                        ? isDarkMode
                          ? "bg-indigo-500 text-white"
                          : "bg-indigo-600 text-white"
                        : isDarkMode
                          ? "bg-slate-700 text-slate-200"
                          : "bg-slate-100 text-slate-700"
                    )}
                  >
                    {num}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={cbCn("block font-semibold leading-snug", isDarkMode ? "text-slate-100" : "text-slate-900")}>
                      {name}
                    </span>
                    <span className={cbCn("mt-0.5 block text-xs", cb.bodySm(isDarkMode))}>
                      {added
                        ? translateUi("All added")
                        : translateUi("{{n}} criteria · {{e}} elements", {
                            n,
                            e: unit.elements.length,
                          })}
                      {selectedInUnit > 0 ? ` · ${selectedInUnit} ${translateUi("selected")}` : ""}
                    </span>
                  </span>
                  <span className={cbCn("mt-1.5 shrink-0 text-slate-400", isUnitExpanded && "rotate-180")}>
                    <ChevronDown className="h-4 w-4" />
                  </span>
                </button>
                {pickable && (
                  <button
                    type="button"
                    disabled={unitVisiblePayloads.length === 0}
                    onClick={() => onAddWhole(unitVisiblePayloads)}
                    className={cbCn(
                      "mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-40",
                      isDarkMode
                        ? "bg-indigo-500/20 text-indigo-200 hover:bg-indigo-500/30"
                        : "bg-indigo-50 text-indigo-800 hover:bg-indigo-100"
                    )}
                    title={translateUi("Add entire unit (Demonstrate/Explain auto-mapped)")}
                  >
                    <Plus className="h-3.5 w-3.5" aria-hidden />
                    {translateUi("Add")}
                  </button>
                )}
              </div>

              {isUnitExpanded && (
                <div className={cbCn("border-t px-3 pb-3 pt-2", isDarkMode ? "border-slate-700" : "border-slate-100")}>
                  {pickable && selectedInUnit > 0 && (
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => onAddSelectedTo(unitVisiblePayloads, "demonstrate")}
                        className={cb.accentBtn(isDarkMode, "emerald")}
                      >
                        {translateUi("Add {{n}} to {{label}}", { n: selectedInUnit, label: demoLabel })}
                      </button>
                      <button
                        type="button"
                        onClick={() => onAddSelectedTo(unitVisiblePayloads, "explain")}
                        className={cb.accentBtn(isDarkMode, "amber")}
                      >
                        {translateUi("Add {{n}} to {{label}}", { n: selectedInUnit, label: explainLabel })}
                      </button>
                      <button
                        type="button"
                        onClick={() => onClearSelected(unitVisibleKeys)}
                        className={cbCn("text-xs font-medium", isDarkMode ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-800")}
                      >
                        {translateUi("Clear")}
                      </button>
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <ElementBlock cycleId={cycleId} unit={unit} unitKey={unitKey} compact tree={tree} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div>
      {units.map((unit) => {
        const unitKey = `${cycleId}-${unit.id}`;
        const isUnitExpanded = expandedUnits[unitKey] ?? defaultExpandUnits;
        const unitVisiblePayloads = collectVisiblePayloadsForUnit(cycleId, unit, items, keyCounts);
        const unitVisibleKeys = unitVisiblePayloads.map((p) => p.libraryKey).filter(Boolean) as string[];
        const selectedInUnit = unitVisibleKeys.filter((k) => !!selectedLibrary[k]).length;
        const hasUnitVisible = unitVisibleKeys.length > 0;
        const allUnitSelected = hasUnitVisible && selectedInUnit === unitVisibleKeys.length;
        const n = unit.elements.reduce(
          (a, el) => a + countVisibleTemplateLinesInElement(cycleId, unit.id, el, items, keyCounts),
          0
        );
        const title = unitTitle ? unitTitle(unit) : unit.title;
        return (
          <div key={unitKey} className={cbCn("border-t", isDarkMode ? "border-slate-700" : "border-slate-200")}>
            <div className={cbCn(`flex w-full items-center gap-2 text-sm ${isMobile ? "px-4 py-3" : "px-4 py-2"}`)}>
              {pickable && (
                <TriCheckbox
                  checked={allUnitSelected}
                  indeterminate={selectedInUnit > 0 && !allUnitSelected}
                  disabled={!hasUnitVisible}
                  ariaLabel={translateUi("Select all criteria in {{title}}", { title })}
                  onChange={(next) => onSetPayloadsSelected(unitVisiblePayloads, next)}
                />
              )}
              <button
                type="button"
                onClick={() => onToggleUnit(unitKey)}
                className={cbCn(`flex min-w-0 flex-1 items-center gap-2 text-left ${isMobile ? "min-h-[44px]" : ""}`, cb.rowHover(isDarkMode))}
              >
                {isUnitExpanded ? (
                  <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" />
                ) : (
                  <ChevronRight className="h-4 w-4 shrink-0 text-slate-500" />
                )}
                <span className={cbCn("font-medium", isDarkMode ? "text-slate-200" : "text-slate-700")}>{title}</span>
                <span className="text-xs text-slate-500">({n} lines)</span>
              </button>
              {pickable && (
                <button
                  type="button"
                  disabled={unitVisiblePayloads.length === 0}
                  onClick={() => onAddWhole(unitVisiblePayloads)}
                  className={cbCn(
                    "rounded border px-2 py-1 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50",
                    isDarkMode ? "border-indigo-500/40 bg-indigo-950/40 text-indigo-200" : "border-indigo-200 bg-indigo-50 text-indigo-900"
                  )}
                  title={translateUi("Add entire unit (Demonstrate/Explain auto-mapped)")}
                >
                  {translateUi("Add whole unit")}
                </button>
              )}
            </div>
            {isUnitExpanded && pickable && selectedInUnit > 0 && (
              <div
                className={`flex flex-wrap items-center gap-2 border-t ${cb.divider(isDarkMode)} ${
                  isDarkMode ? "bg-slate-800/50" : "bg-slate-50"
                } ${isMobile ? "px-4 py-3" : "px-4 py-2"}`}
              >
                <button type="button" onClick={() => onAddSelectedTo(unitVisiblePayloads, "demonstrate")} className={cb.accentBtn(isDarkMode, "emerald")}>
                  {translateUi("Add selected to {{label}}", { label: demoLabel })}
                </button>
                <button type="button" onClick={() => onAddSelectedTo(unitVisiblePayloads, "explain")} className={cb.accentBtn(isDarkMode, "amber")}>
                  {translateUi("Add selected to {{label}}", { label: explainLabel })}
                </button>
                <button
                  type="button"
                  onClick={() => onClearSelected(unitVisibleKeys)}
                  className={cbCn("text-xs font-medium", isDarkMode ? "text-slate-400" : "text-slate-600")}
                >
                  {translateUi("Clear selected")}
                </button>
              </div>
            )}
            {isUnitExpanded && <ElementBlock cycleId={cycleId} unit={unit} unitKey={unitKey} tree={tree} />}
          </div>
        );
      })}
    </div>
  );
}
